import { TagsIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
  BlockSeparator,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasInput,
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  TextCategorizationShape,
  textCategorizationType,
  comparisonOperators,
  ComparisonOperator,
  TextCategorizationProps,
  metadataType,
  Dimensions,
  standardCategories,
} from "@/components/canvas-editor/shapes/shapes";
import {
  MIN_H,
  ToolStateNode,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { Label } from "@/components/ui/label";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function isTextCategorizationShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is TextCategorizationShape {
  return shape.type === textCategorizationType;
}

export class TextCategorizationTool extends ToolStateNode {
  static override id = textCategorizationType;

  static override uiToolDef = (editor: Editor) => ({
    id: textCategorizationType,
    icon: TagsIcon,
    label: TextCategorizationShapeUtil.docs.title,
    defaultColor: TextCategorizationShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(textCategorizationType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={TextCategorization}
        util={TextCategorizationShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: textCategorizationType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class TextCategorizationShapeUtil extends BindableUtil<TextCategorizationShape> {
  static override migrations = createMigrations(textCategorizationType);
  static override type = textCategorizationType;

  static override docs = {
    defaultColor: "pink" as const,
    title: "Text Categorization",
    description:
      "Use AI to analyze if text matches any arbitrary category or concept. Enter any label and the model will assess how well the content matches that concept.",
  };

  override getType() {
    return textCategorizationType;
  }

  static override props: RecordProps<TextCategorizationShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    tag: T.string,
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 360,
      minW: 400,
      h: MIN_H,
      tag: "",
      operator: ">=" as const,
      value: 0.5,
      metadata: undefined,
    };
  }

  override component(shape: TextCategorizationShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <TextCategorization
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function TextCategorization({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: TextCategorizationShape;
  className?: string;
  updateShape: (
    shape: TextCategorizationShape,
    props: Partial<TextCategorizationProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );
  const isReadOnly = useIsReadonlyEditor();
  const isUsingDeprecatedTag =
    shape.props.tag && !standardCategories.includes(shape.props.tag);

  if (isUsingDeprecatedTag) {
    return (
      <BlockContainerShapeContext value={shape}>
        <BlockContainer
          resultState={shape.props.resultState}
          pattern={shape.props.metadata?.pattern}
          isBound={isBound}
          className={cn(className, "animate-pulse")}
          variant={
            shape.props.metadata?.color ||
            TextCategorizationShapeUtil.docs.defaultColor
          }
          isSelected={isSelected}
          onUpdateDimensions={onUpdateDimensions}
          dynamicHeight={true}
        >
          <NodeBlockHeader
            props={shape.props}
            docs={TextCategorizationShapeUtil.docs}
            isDeprecated={true}
          />
          <BlockBody>
            <div className="flex flex-col gap-2 flex-1">
              <div className="flex gap-2">
                <SimpleTooltip message="Enter any category or concept to analyze">
                  <CanvasInput
                    aria-label="Category"
                    value={shape.props.tag}
                    type="text"
                    className="w-[200px] shrink-0"
                    placeholder="Enter category..."
                    onValueChange={(v) => updateShape(shape, { tag: v })}
                  />
                </SimpleTooltip>
                <PercentOperatorSelect
                  value={shape.props.operator}
                  onValueChange={(v) =>
                    updateShape(shape, { operator: v as ComparisonOperator })
                  }
                />
                <SimpleTooltip message={modelProbabilityExplanation}>
                  <CanvasPercentageInput
                    aria-label="Threshold"
                    value={shape.props.value as number}
                    className="flex-1"
                    placeholder="Threshold"
                    onValueChange={(value) => updateShape(shape, { value })}
                  />
                </SimpleTooltip>
              </div>
              {!isReadOnly && (
                <>
                  <BlockSeparator />
                  <div className="flex flex-col gap-2 py-2">
                    <Label>Please choose a standard category</Label>
                    <CanvasSimpleSelect
                      aria-label="Standard Category"
                      options={standardCategories}
                      placeholder="Choose a standard category"
                      onValueChange={(v) => updateShape(shape, { tag: v })}
                    />
                  </div>
                </>
              )}
            </div>
          </BlockBody>
        </BlockContainer>
      </BlockContainerShapeContext>
    );
  }

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        variant={
          shape.props.metadata?.color ||
          TextCategorizationShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={TextCategorizationShapeUtil.docs}
        />
        <BlockBody>
          <SimpleTooltip message="Enter any category or concept to analyze">
            <CanvasSimpleSelect
              aria-label="Standard Category"
              options={standardCategories}
              placeholder="Choose a standard category"
              value={shape.props.tag}
              className="w-[200px] shrink-0"
              onValueChange={(v) => updateShape(shape, { tag: v })}
            />
          </SimpleTooltip>
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
